/**
 * INCSUBNotification.jsx
 *
 * This component represents the Notification section. It fetches and displays the user's notifications.
 * It also handles marking notifications as read, either individually or all at once.
 *
 * @component
 * @example
 * // Usage of INCSUBNotification component
 * <INCSUBNotification isOpen={isOpen} />
 *
 * @author Govind Dindewad
 */

import React, { useEffect, useState } from 'react';
import './notification.css';
import INCSUBNotificationInfoCard from './notificationCard';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { getAllNotifications, markAsRead } from '../../services/notificationServices';
import { useDispatch, useSelector } from 'react-redux';
import { setNotificationData } from '../../features/notifications/notificationSlice';
import { update } from 'lodash';

/**
 * INCSUBNotification Component
 *
 * This component fetches and displays a list of notifications for the current user. 
 * It also provides the functionality to mark individual notifications or all notifications as read.
 *
 * @param {Object} isOpen - Flag to check if the notification panel is open
 * @returns {JSX.Element} Rendered Notification component
 * 
 * @author Govind Dindewad
 */
function INCSUBNotification(isOpen) {
    const navigate = useNavigate();  // Used for navigation
    const notificationData = useSelector((state) => state.notification.notificationData);  // Get notifications from Redux store
    const [notificationsDtoList, setNotificationList] = useState();  // Local state to hold notifications
    const dispatch = useDispatch();  // Redux dispatch function
    const [pageNo, setPageNo] = useState(0);

    /**
     * fetchNotifications
     *
     * This function fetches all notifications from the server.
     * It is called when the component is mounted to populate the notification list.
     *
     * @returns {void}
     * @author Govind Dindewad
     */
    const fetchNotifications = async (loadMore) => {
        console.log(loadMore)
        if (loadMore === true) {
            setPageNo(pageNo + 1);
        }
        const userId = JSON.parse(window.localStorage.getItem('userData')).userId;
        try {
            // Call API to get all notifications for the user
            console.log(pageNo)
            const notificationFetchData = await getAllNotifications(userId, pageNo);
            if (notificationFetchData.status === 200) {
                if (loadMore === true) {
                    const updateList = [...notificationData.notificationsDtoList];
                    updateList.push(...notificationFetchData.data.data.notificationsDtoList);
                    const updatedData = [{ ...notificationFetchData.data.data, notificationsDtoList: updateList }]
                    dispatch(setNotificationData(updatedData[0]));
                     setNotificationList(updateList);
                } else {
                    setNotificationList(notificationFetchData.data.data.notificationsDtoList);
                    dispatch(setNotificationData(notificationFetchData.data.data));  // Store notifications in Redux state
                }



            } else if (notificationFetchData.status === 301) {
                toast.error("Network Error");
            } else if (notificationFetchData.status === 500) {
                toast.error("Internal server error");
            } else if (notificationFetchData.status === 401) {
                // Handle unauthorized access
                window.localStorage.setItem('userData', null);
                navigate('/login');
            } else {
                toast.error(notificationFetchData.data.message);  // Show error message
            }
        } catch (error) {
            console.log(error)
            toast.error("Something went wrong");
        }
    };

    /**
     * handleAllReadNotification
     *
     * This function handles marking all notifications as read at once.
     * It sends a list of notification IDs to the server to mark them as read.
     *
     * @returns {void}
     * @author Govind Dindewad
     */
    const handleAllReadNotification = async () => {
        const userId = JSON.parse(window.localStorage.getItem('userData')).userId;
        const readList = notificationsDtoList?.map((notification) => notification.notificationId) || [];

        const readListObj = {
            userId: userId,
            notificationIdList: readList
        };

        try {
            // Call API to mark notifications as read
            const readData = await markAsRead(readListObj);

            if (readData.status === 200) {
                dispatch(setNotificationData({
                    ...notificationData,
                    notificationsDtoList: notificationsDtoList.map((notification) => ({
                        ...notification,
                        read: true
                    }))
                }))
                // Update the local state to reflect that all notifications are read
                setNotificationList((prevNotifications) =>
                    notificationsDtoList.map((notification) => ({
                        ...notification,
                        read: true
                    }))
                );
            } else if (readData.status === 301) {
                toast.error("Network Error");
            } else if (readData.status === 500) {
                toast.error("Internal server error");
            } else if (readData.status === 401) {
                // Handle unauthorized access
                window.localStorage.setItem('userData', null);
                navigate('/login');
            } else {
                toast.error(readData.data.message);
            }
        } catch (error) {
            toast.error("Something went wrong");
        }
    };

    /**
     * handleSingleRead
     *
     * This function handles marking a single notification as read when the user clicks on it.
     * It sends the notification ID to the server to mark it as read.
     *
     * @param {string} notificationId - The ID of the notification to mark as read
     * @returns {void}
     * @author Govind Dindewad
     */
    const handleSingleRead = async (notificationId) => {
        const userId = JSON.parse(window.localStorage.getItem('userData')).userId;
        const readListObj = {
            userId: userId,
            notificationIdList: [notificationId]
        };

        try {
            // Call API to mark the specific notification as read
            const readData = await markAsRead(readListObj);
            if (readData.status === 200) {
                // Update the local state to mark the specific notification as read
                console.log(notificationData)
                dispatch(setNotificationData({
                    ...notificationData,
                    notificationsDtoList: notificationsDtoList.map((notification) =>
                        notification.notificationId === notificationId
                            ? { ...notification, read: true }
                            : notification
                    )
                }))
                setNotificationList((prevNotifications) =>
                    prevNotifications.map((notification) =>
                        notification.notificationId === notificationId
                            ? { ...notification, read: true }
                            : notification
                    )
                );
            } else if (readData.status === 301) {
                toast.error("Network Error");
            } else if (readData.status === 500) {
                toast.error("Internal server error");
            } else if (readData.status === 401) {
                // Handle unauthorized access
                window.localStorage.setItem('userData', null);
                navigate('/login');
            } else {
                toast.error(readData.data.message);
            }
        } catch (error) {
            console.log(error)
            toast.error("Something went wrong");
        }
    };

    /**
     * useEffect Hook
     *
     * This hook is called when the component mounts. It triggers the fetchNotifications function to load the notifications.
     *
     * @returns {void}
     * @author Govind Dindewad
     */
    useEffect(() => {
        fetchNotifications(pageNo);
    }, []);

    /**
     * renderedNotificationCard
     *
     * This variable maps over the notification data and creates an array of INCSUBNotificationInfoCard components to display notifications.
     *
     * @returns {JSX.Element[]} Array of notification cards
     * @author Govind Dindewad
     */
    const renderedNotificationCard = notificationData && notificationData.notificationsDtoList && notificationData.notificationsDtoList.length > 0 && notificationData.notificationsDtoList.map((notification, index) => {
        return <INCSUBNotificationInfoCard notification={notification} index={index} handleRead={handleSingleRead} />;
    });

    return (
        <div className='INCSUBNotification_ctn'>
            {/* Side Panel for Notifications */}
            <div className='INCSUBNotification_ctn_side_panel'>
                <div className='INCSUBNotification_side_panle_head'>
                    {/* Header with title and "Mark All as Read" button */}
                    <div className='INCSUBNotification_side_panle_heading'>Notification</div>
                    <span className='INCSUBNotification_side_panle_readAll' onClick={() => handleAllReadNotification()}>
                        Mark All as Read
                    </span>
                </div>
                <div className='INCSUBNotification_card_holder_ctn'>
                    <div className='INCSUBNotification_card_holder_scroller'>
                        {/* Display rendered notification cards */}
                        {renderedNotificationCard}
                    </div>
                </div>
                {notificationData && !notificationData.isLastPage ? <div className='INCSUB_load_more_notification' onClick={() => {
                    fetchNotifications(true)
                }}>Load More...</div> : null}
            </div>
        </div>
    );
}

export default INCSUBNotification;
